import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DashboardStats,
  RecentDealActivity,
  DealActivityPoint,
  DealTypeMonthlyPoint,
  dashboardService
} from '@/services/dashboardService';

interface UseDashboardOptions {
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
  recentDays?: number;
  searchDebounceMs?: number;
}

interface UseDashboardReturn {
  stats: DashboardStats | null;
  statsLoading: boolean;
  statsError: string | null;

  deals: RecentDealActivity[];
  dealsLoading: boolean;
  dealsError: string | null;

  activityTrend: DealActivityPoint[];
  activityLoading: boolean;
  activityError: string | null;

  dealTypeTrend: DealTypeMonthlyPoint[];
  dealTypeLoading: boolean;
  dealTypeError: string | null;

  loading: boolean;

  refetchStats: () => Promise<void>;
  refetchDeals: () => Promise<void>;
  refetchActivity: () => Promise<void>;
  refetchDealTypes: () => Promise<void>;
  refetchAll: () => Promise<void>;
}

export const useDashboard = (options: UseDashboardOptions = {}): UseDashboardReturn => {
  const {
    limit = 10,
    search,
    status,
    type,
    recentDays = 30,
    searchDebounceMs = 300,
  } = options;

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [deals, setDeals] = useState<RecentDealActivity[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [dealsError, setDealsError] = useState<string | null>(null);

  const [activityTrend, setActivityTrend] = useState<DealActivityPoint[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState<string | null>(null);

  const [dealTypeTrend, setDealTypeTrend] = useState<DealTypeMonthlyPoint[]>([]);
  const [dealTypeLoading, setDealTypeLoading] = useState(true);
  const [dealTypeError, setDealTypeError] = useState<string | null>(null);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const data = await dashboardService.getDashboardStats();
      if (aliveRef.current) setStats(data);
    } catch (err) {
      if (aliveRef.current) {
        setStatsError(err instanceof Error ? err.message : 'Failed to fetch dashboard stats');
        console.error('Error fetching dashboard stats:', err);
      }
    } finally {
      if (aliveRef.current) setStatsLoading(false);
    }
  }, []);

  const fetchDealsNow = useCallback(async (args: {
    limit: number; search?: string; status?: string; type?: string; recentDays: number;
  }) => {
    try {
      setDealsLoading(true);
      setDealsError(null);
      const rows = await dashboardService.getRecentDealActivity(
        args.limit, args.search, args.status, args.type, args.recentDays
      );
      if (aliveRef.current) setDeals(rows);
    } catch (err) {
      if (aliveRef.current) {
        setDealsError(err instanceof Error ? err.message : 'Failed to fetch recent deals');
        console.error('Error fetching recent deals:', err);
      }
    } finally {
      if (aliveRef.current) setDealsLoading(false);
    }
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      setActivityError(null);
      const trend = await dashboardService.getDealActivityTrend();
      if (aliveRef.current) setActivityTrend(trend);
    } catch (err) {
      if (aliveRef.current) {
        setActivityError(err instanceof Error ? err.message : 'Failed to fetch activity trend');
        console.error('Error fetching activity trend:', err);
      }
    } finally {
      if (aliveRef.current) setActivityLoading(false);
    }
  }, []);

  const fetchDealTypes = useCallback(async () => {
    try {
      setDealTypeLoading(true);
      setDealTypeError(null);
      const trend = await dashboardService.getDealTypeTrend();
      if (aliveRef.current) setDealTypeTrend(trend);
    } catch (err) {
      if (aliveRef.current) {
        setDealTypeError(err instanceof Error ? err.message : 'Failed to fetch deal type trend');
        console.error('Error fetching deal type trend:', err);
      }
    } finally {
      if (aliveRef.current) setDealTypeLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchActivity();
    fetchDealTypes();
  }, [fetchStats, fetchActivity, fetchDealTypes]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchDealsNow({ limit, search, status, type, recentDays });
    }, searchDebounceMs);
    return () => clearTimeout(t);
  }, [limit, search, status, type, recentDays, searchDebounceMs, fetchDealsNow]);

  const loading = statsLoading || dealsLoading || activityLoading || dealTypeLoading;

  const refetchDeals = useCallback(
    () => fetchDealsNow({ limit, search, status, type, recentDays }),
    [fetchDealsNow, limit, search, status, type, recentDays]
  );

  const refetchAll = useCallback(async () => {
    await Promise.all([fetchStats(), refetchDeals(), fetchActivity(), fetchDealTypes()]);
  }, [fetchStats, refetchDeals, fetchActivity, fetchDealTypes]);

  return {
    stats, statsLoading, statsError,
    deals, dealsLoading, dealsError,
    activityTrend, activityLoading, activityError,
    dealTypeTrend, dealTypeLoading, dealTypeError,
    loading,
    refetchStats: fetchStats,
    refetchDeals,
    refetchActivity: fetchActivity,
    refetchDealTypes: fetchDealTypes,
    refetchAll,
  };
};
